import React, { useState } from 'react';

import ComboBox, { Props as ComboBoxProps } from 'ds/ComboBox';
import ComboBoxResult from 'ds/ComboBox/ComboBoxResult';
import { CompactBuildingUnitWithCustomer, formatAddress } from 'shared';

export type Props = Omit<ComboBoxProps<number>, 'results' | 'image' | 'onChange' | 'label'> &
  Partial<Pick<ComboBoxProps<number>, 'onChange' | 'label'>> & {
    buildingUnits: Pick<CompactBuildingUnitWithCustomer, 'id' | 'name' | 'address' | 'customer'>[];
    selectedBuildingUnitId?: number | null;
    onCreateNew?: VoidFunction;
  };

const BuildingUnitComboBox: React.FC<Props> = ({
  buildingUnits,
  selectedBuildingUnitId,
  label = 'Building Unit',
  onChange,
  onSelect,
  onCreateNew,
  ...props
}) => {
  const buildingUnit = buildingUnits.find(bu => bu.id === selectedBuildingUnitId);
  const [buildingUnitQuery, setBuildingUnitQuery] = useState<null | string>(buildingUnit?.name || null);

  return (
    <ComboBox
      required={false}
      label={label}
      placeholder="Building Unit"
      size="sm"
      results={[
        ...(buildingUnitQuery
          ? buildingUnits.filter(
              bu =>
                bu.name.toLowerCase().includes(buildingUnitQuery.toLowerCase()) ||
                formatAddress(bu.address).toLowerCase().includes(buildingUnitQuery.toLowerCase()) ||
                bu.customer?.name?.toLowerCase().includes(buildingUnitQuery.toLowerCase())
            )
          : buildingUnits
        ).map(bu => ({
          value: bu.id,
          element: (
            <ComboBoxResult
              label={`${bu.name} - ${formatAddress(bu.address)}`}
              key={bu.id}
              query={buildingUnitQuery || ''}
              subLabel={bu.customer?.name}
            />
          )
        })),
        ...(onCreateNew
          ? [
              {
                value: -1, // Special value for create new
                element: (
                  <ComboBoxResult label="+ Create New Building Unit" key="create-new" query={buildingUnitQuery || ''} />
                )
              }
            ]
          : [])
      ]}
      query={
        selectedBuildingUnitId && buildingUnitQuery === null
          ? buildingUnits.find(bu => bu.id === selectedBuildingUnitId)?.name
          : buildingUnitQuery || ''
      }
      onChange={params => {
        setBuildingUnitQuery(params.value);
        onChange?.(params);
      }}
      onSelect={value => {
        setBuildingUnitQuery(null);
        if (value === -1 && onCreateNew) {
          onCreateNew();
        } else {
          onSelect(value);
        }
      }}
      showResultsWhenEmpty
      {...props}
    />
  );
};

export default BuildingUnitComboBox;
